import { defineStore } from 'pinia'
import {
  getEntitiesQry,
  postEntityQry,
  patchEntityQry,
  deleteEntityQry,
  moveEntityQry,
  attachUserToEntityQry,
  getEntityMembersQry,
  detachUserFromEntityQry,
  getZonesQry,
  postZoneQry,
  patchZoneQry,
  deleteZoneQry,
} from '../services/organization.service'


/* Turn the flat entity list returned by the API into a nested tree.
   Each node gets a `children` array; roots are nodes with parent === null. */
function buildTree(flatList) {
  const byId = {}
  flatList.forEach(e => { byId[e.id] = { ...e, children: [] } })

  const roots = []
  flatList.forEach(e => {
    const node = byId[e.id]
    if (e.parent && byId[e.parent]) {
      byId[e.parent].children.push(node)
    } else {
      roots.push(node)
    }
  })
  return roots
}


export const useOrganizationStore = defineStore('organization', {
  state: () => ({
    entities: [],   // flat list from the API
    zones: [],      // zones of the current client
    loading: false,
    zonesLoading: false,
    // Last client the tree was scoped to (operator client picker). Mutations reuse
    // it so a refresh after attach/move/… keeps the same client — not "all clients".
    currentClientId: null,
  }),

  getters: {
    // Nested tree for the iView <Tree> component.
    tree(state) {
      return buildTree(state.entities)
    },
    // Handy for select inputs (label = name).
    entityOptions(state) {
      return state.entities.map(e => ({ value: e.id, label: e.name }))
    },
    // Resolve an entity name by id (for displaying a zone's parent entity).
    entityNameById(state) {
      const map = {}
      state.entities.forEach(e => { map[e.id] = e.name })
      return (id) => map[id] || ''
    },
  },

  actions: {
    async fetchEntities(clientId = null) {
      // Remember the scope so later refreshes (after a mutation) reuse the same
      // client instead of silently falling back to "all clients".
      this.currentClientId = clientId
      this.loading = true
      try {
        const params = clientId ? { client: clientId } : {}
        const res = await getEntitiesQry(params)
        // Support both paginated ({results}) and plain-list responses.
        this.entities = Array.isArray(res.data) ? res.data : (res.data.results || [])
      } finally {
        this.loading = false
      }
    },

    // Refresh the tree keeping the current client scope (used after mutations).
    async refreshEntities() {
      await this.fetchEntities(this.currentClientId)
    },

    async createEntity(data) {
      const res = await postEntityQry(data)
      await this.refreshEntities()
      return res.data
    },

    async updateEntity(id, data) {
      const res = await patchEntityQry(id, data)
      await this.refreshEntities()
      return res.data
    },

    async removeEntity(id) {
      await deleteEntityQry(id)
      await this.refreshEntities()
    },

    async moveEntity(id, parentId) {
      const res = await moveEntityQry(id, parentId)
      await this.refreshEntities()
      return res.data
    },

    async attachUser(entityId, userId) {
      const res = await attachUserToEntityQry(entityId, userId)
      await this.refreshEntities()  // refresh member counts in the tree
      return res.data
    },

    async fetchEntityMembers(entityId, params = {}) {
      const res = await getEntityMembersQry(entityId, params)
      // Standard DRF envelope: {count, next, previous, results}
      return { results: res.data.results || [], count: res.data.count || 0 }
    },

    async detachUser(entityId, userId) {
      const res = await detachUserFromEntityQry(entityId, userId)
      await this.refreshEntities()  // refresh member counts in the tree
      return res.data
    },

    /* ---- Zones (F3) ---- */

    async fetchZones(clientId = null) {
      this.currentClientId = clientId  // keep the scope for post-mutation refreshes
      this.zonesLoading = true
      try {
        const params = clientId ? { client: clientId } : {}
        const res = await getZonesQry(params)
        this.zones = Array.isArray(res.data) ? res.data : (res.data.results || [])
      } finally {
        this.zonesLoading = false
      }
    },

    // Refresh zones keeping the current client scope (used after mutations).
    async refreshZones() {
      await this.fetchZones(this.currentClientId)
    },

    async createZone(data) {
      const res = await postZoneQry(data)
      await this.refreshZones()
      return res.data
    },

    async updateZone(id, data) {
      const res = await patchZoneQry(id, data)
      await this.refreshZones()
      return res.data
    },

    async removeZone(id) {
      await deleteZoneQry(id)
      await this.refreshZones()
    },
  },
})
